import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw, Trash2, History, Database, ShieldAlert, RefreshCw } from "lucide-react";

const TYPE_COLORS = {
  Project: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  Client: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Invoice: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  Expense: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  BOQ: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",
  File: "bg-pink-500/15 text-pink-600 dark:text-pink-400",
  "Finance Record": "bg-rose-500/15 text-rose-600 dark:text-rose-400",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function RecoveryCenter({ user }) {
  const [tab, setTab] = useState("deleted");
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {}, backups: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const isSuperAdmin = user?.role === "super_admin";

  const load = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const [it, au, bk, st] = await Promise.all([
        api.get("/recovery/items"),
        api.get("/recovery/audit?limit=100"),
        api.get("/recovery/backups"),
        api.get("/recovery/stats"),
      ]);
      setItems(it.data || []);
      setAudit(au.data || []);
      setBackups(bk.data || []);
      setStats(st.data || { total: 0, by_type: {}, backups: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Recovery Center");
    } finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const restore = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/recovery/${row.id}/restore`);
      toast.success(`Restored ${row.entity_type}: ${row.label}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusyId(null); }
  };

  const purge = async (row) => {
    if (!window.confirm(`Permanently delete "${row.label}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/recovery/${row.id}`);
      toast.success("Permanently deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(null); }
  };

  const emptyAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`Permanently delete ALL ${items.length} item(s) in Recently Deleted? This cannot be undone.`)) return;
    try {
      await api.post("/recovery/empty");
      toast.success("Recently Deleted emptied");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to empty"); }
  };

  if (!isSuperAdmin) return null;

  const tabs = [
    { key: "deleted", label: `Recently Deleted (${stats.total})`, icon: RotateCcw, testid: "recovery-tab-deleted" },
    { key: "audit", label: "Audit Log", icon: History, testid: "recovery-tab-audit" },
    { key: "backups", label: `Backups (${stats.backups})`, icon: Database, testid: "recovery-tab-backups" },
  ];

  return (
    <section className="rounded-2xl p-6 bg-card border border-border/60 space-y-4" data-testid="recovery-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Backup & Recovery
          </div>
          <div className="text-sm font-medium mt-1">Recovery Center</div>
          <div className="text-xs text-muted-foreground mt-0.5">Restore or permanently remove deleted records. Super Admin only.</div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="recovery-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-1.5 border-b border-border/60">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "deleted" && (
        <div className="space-y-3" data-testid="recovery-deleted-panel">
          {items.length > 0 && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={emptyAll} className="text-destructive hover:text-destructive" data-testid="recovery-empty-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Empty Recently Deleted
              </Button>
            </div>
          )}
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Nothing in Recently Deleted. Deleted records will appear here.</div>
          ) : (
            <div className="space-y-2">
              {items.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/50 bg-muted/20" data-testid="recovery-item">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[row.entity_type] || "bg-muted text-muted-foreground"}`}>{row.entity_type}</span>
                      <span className="text-sm font-medium truncate">{row.label}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Deleted by {row.deleted_by_name || "—"} · {fmt(row.deleted_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => restore(row)} disabled={busyId === row.id} data-testid="recovery-restore-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => purge(row)} disabled={busyId === row.id} className="text-destructive hover:text-destructive" data-testid="recovery-purge-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-2" data-testid="recovery-audit-panel">
          {audit.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No activity recorded yet.</div>
          ) : (
            audit.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-border/40 text-xs">
                <div className="min-w-0">
                  <span className="font-medium capitalize">{(a.action || "").replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground"> · {a.entity}{a.meta?.label ? ` · ${a.meta.label}` : ""}</span>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0">{a.user_name || "—"} · {fmt(a.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "backups" && (
        <div className="space-y-2" data-testid="recovery-backups-panel">
          <div className="text-[11px] text-muted-foreground">Automatic snapshots taken before bulk imports (leads, expenses, employees). Last 20 are kept.</div>
          {backups.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No backups yet. One is created automatically before each bulk import.</div>
          ) : (
            backups.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-border/40 text-xs">
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.label}</div>
                  <div className="text-[11px] text-muted-foreground">{b.doc_count} record(s) · {(b.collections || []).join(", ")}</div>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0">{b.created_by_name || "—"} · {fmt(b.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}
